import { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import {
  Alert,
  Box,
  Button,
  Chip,
  CircularProgress,
  Link,
  Paper,
  Stack,
  Tooltip,
  Typography,
} from '@mui/material';
import { ArrowBackRounded, PictureAsPdfRounded } from '@mui/icons-material';
import { collection, doc, getDoc, getDocs, onSnapshot } from 'firebase/firestore';
import { formatDateOnly, todayInZoneWeb } from '@dosfilos/shared';
import {
  buildProgramLadder,
  ladderColumnLabel,
  type LadderProgram,
  type SubjectAreaCode,
} from '@dosfilos/domain';
import { db } from '../../lib/firebase';
import { useTenantDateFormat, useTenantStore, useTenantTimeZone } from '../../stores/tenant.store';
import {
  buildLadderPdfData,
  buildProgramLadderPdfLabels,
  ladderPdfNotes,
  type LadderCourseInfo,
} from '../../lib/program-ladder-pdf';
import { renderWithoutHyphenation } from '../../lib/pdf-hyphenation';
import { openGeneratedPdf } from '../../lib/open-generated-pdf';
import { errorText } from '../../lib/error-text';
import { toLadderProgram } from '../../lib/program-ladder';
import { AREA_COLOR, NO_AREA_COLOR } from '../../lib/subject-area-colors';

// ─── Program ladder ──────────────────────────────────────────────────────────
// Every program's malla in one grid, placed where the seminary's spreadsheet has
// it: a row per program, a column per ladder period. Positions come from the
// "builds on" edges (services/program-ladder.service.ts), never from the term
// labels. Read-only; each program is edited on its own screen.

interface CourseInfo {
  name: string;
  code: string;
  credits: number;
  inactive: boolean;
  area?: SubjectAreaCode;
}

const NAME_COL = 220;
/** Minimum width of a year column; wider screens stretch it. */
const TERM_COL = 196;

export function ProgramLadderPage(): JSX.Element {
  const { t } = useTranslation('sis');
  const { t: tCap } = useTranslation('cap');
  const navigate = useNavigate();
  const { currentTenantId } = useTenantStore();
  const tz = useTenantTimeZone();
  const dateFormat = useTenantDateFormat();
  const [exporting, setExporting] = useState(false);
  const [exportError, setExportError] = useState<string | null>(null);

  const [programs, setPrograms] = useState<LadderProgram[] | null>(null);
  const [courses, setCourses] = useState<Map<string, CourseInfo>>(new Map());
  const [areas, setAreas] = useState<Record<string, SubjectAreaCode>>({});

  useEffect(() => {
    if (!currentTenantId) return;
    const unsub = onSnapshot(collection(db, `tenants/${currentTenantId}/programs`), (snap) =>
      setPrograms(snap.docs.map((d) => toLadderProgram(d.id, d.data()))),
    );
    return () => unsub();
  }, [currentTenantId]);

  // All courses, inactive included: a plan that lists an inactive course must
  // still name it (and say so), not print an id.
  useEffect(() => {
    if (!currentTenantId) return;
    const unsub = onSnapshot(collection(db, `tenants/${currentTenantId}/courses`), (snap) => {
      const m = new Map<string, CourseInfo>();
      for (const d of snap.docs) {
        const data = d.data();
        m.set(d.id, {
          name: (data['name'] as string | undefined) ?? '',
          code: (data['code'] as string | undefined) ?? '',
          credits: (data['credits'] as number | undefined) ?? 0,
          inactive: ((data['status'] as string | undefined) ?? 'active') !== 'active',
        });
      }
      setCourses(m);
    });
    return () => unsub();
  }, [currentTenantId]);

  // CAP subject area per course, for the card color — same source as the malla.
  useEffect(() => {
    if (!currentTenantId) return;
    let alive = true;
    void getDocs(collection(db, `tenants/${currentTenantId}/courseCcds`)).then((snap) => {
      if (!alive) return;
      const map: Record<string, SubjectAreaCode> = {};
      for (const d of snap.docs) {
        const area = (d.data() as { data?: { capSubjectArea?: SubjectAreaCode } }).data
          ?.capSubjectArea;
        if (area) map[d.id] = area;
      }
      setAreas(map);
    });
    return () => {
      alive = false;
    };
  }, [currentTenantId]);

  const ladder = useMemo(() => buildProgramLadder(programs ?? []), [programs]);
  const nameOf = (id: string) => programs?.find((p) => p.id === id)?.name ?? id;
  // CADA CURSO UNA VEZ. El PDF ya deduplicaba y la pantalla no, así que un
  // programa que repite un curso en dos de sus términos mostraba más créditos
  // en pantalla que en su propio plan impreso. Con los dos semestres de un año
  // cayendo en la misma columna, la repetición dejó de ser rara.
  const creditsOf = (ids: string[]) =>
    [...new Set(ids)].reduce((sum, id) => sum + (courses.get(id)?.credits ?? 0), 0);
  const misplacedById = new Map(ladder.misplaced.map((m) => [m.courseId, m.columns]));
  // EL AÑO MANDA SOBRE LA ETIQUETA. En un cruce las etiquetas no coinciden —el
  // Año 2 del Certificado dice «Semestre 1» y el de Predicación «Semestre 2»—
  // y la columna se quedaba sin nombre. El año es un dato, no un texto.
  const columnName = (i: number) =>
    ladderColumnLabel(ladder.columns[i], i, {
      porAnio: (n) => t('programLadder.year', { n }),
      porPosicion: (n) => t('program.curriculum.termNumber', { n }),
    });

  const usedAreas = [
    ...new Set(ladder.columns.flatMap((c) => c.courseIds.map((id) => areas[id]).filter(Boolean))),
  ] as SubjectAreaCode[];

  // Columns STRETCH to the container and only scroll below their minimum. Fixed
  // widths left the grid ending at the last year with an empty strip after it,
  // inside a border drawn for the full width.
  const gridTemplateColumns = `${NAME_COL}px repeat(${ladder.columnCount}, minmax(${TERM_COL}px, 1fr))`;

  // The same ladder, as a printable document with the institutional logo. The
  // renderer and the document are loaded on demand: most visits never print.
  const exportPdf = async () => {
    if (!currentTenantId || !programs) return;
    setExporting(true);
    setExportError(null);
    try {
      // The tab opens inside the click; the PDF is generated into it.
      await openGeneratedPdf(async () => {
        const [{ pdf }, { ProgramLadderPdfDocument }, tenantSnap] = await Promise.all([
          import('@react-pdf/renderer'),
          import('./components/ProgramLadderPdfDocument'),
          getDoc(doc(db, `tenants/${currentTenantId}`)),
        ]);
        const tenant = tenantSnap.data() ?? {};
        const config = (tenant['config'] ?? {}) as Record<string, unknown>;
        // The document logo when the institution set one; else the general logo.
        const logoUrl = (config['documentLogoUrl'] ?? config['logoUrl'] ?? tenant['logoUrl']) as
          | string
          | undefined;
        const pdfCourses = new Map<string, LadderCourseInfo>(
          [...courses].map(([id, c]) => [
            id,
            {
              code: c.code,
              name: c.name,
              credits: c.credits,
              ...(areas[id] ? { area: areas[id] } : {}),
            },
          ]),
        );
        // The PDF helpers take a plain translate function (they also run in Node).
        const tr = (key: string, params?: Record<string, unknown>) => t(key, params ?? {});
        const fallback = (n: number) => t('program.curriculum.termNumber', { n });
        const data = buildLadderPdfData({
          programs,
          courses: pdfCourses,
          tenantName: (tenant['name'] as string | undefined) ?? '',
          ...(logoUrl ? { logoUrl } : {}),
          labels: buildProgramLadderPdfLabels(tr, formatDateOnly(todayInZoneWeb(tz), dateFormat)),
          columnFallback: fallback,
          yearName: (n) => t('programLadder.year', { n }),
          areaLabel: (area) => tCap(`subjectAreas.${area}`),
          noAreaLabel: t('programLadder.pdf.noArea'),
          notes: ladderPdfNotes(tr, pdfCourses, fallback, (n) => t('programLadder.year', { n })),
        });
        return renderWithoutHyphenation(() =>
          pdf(<ProgramLadderPdfDocument data={data} />).toBlob(),
        );
      }, t('programLadder.pdf.title'));
    } catch (e) {
      setExportError(errorText(e, t('programLadder.pdf.exportError')));
    } finally {
      setExporting(false);
    }
  };

  return (
    <Box sx={{ p: { xs: 2, md: 3 } }}>
      <Button
        startIcon={<ArrowBackRounded />}
        onClick={() => navigate('/academic')}
        sx={{ mb: 1 }}
        size="small"
      >
        {t('programLadder.back')}
      </Button>
      <Stack
        direction={{ xs: 'column', sm: 'row' }}
        justifyContent="space-between"
        alignItems={{ sm: 'flex-start' }}
        gap={1.5}
        sx={{ mb: 2 }}
      >
        <Box>
          <Typography variant="h4" fontWeight={700}>
            {t('programLadder.title')}
          </Typography>
          <Typography variant="body2" color="text.secondary">
            {t('programLadder.subtitle')}
          </Typography>
        </Box>
        <Button
          variant="outlined"
          startIcon={
            exporting ? <CircularProgress size={16} color="inherit" /> : <PictureAsPdfRounded />
          }
          onClick={() => void exportPdf()}
          disabled={exporting || !programs || ladder.rows.length === 0}
          sx={{ flexShrink: 0 }}
        >
          {exporting ? t('programLadder.pdf.exporting') : t('programLadder.pdf.export')}
        </Button>
      </Stack>
      {exportError && (
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => setExportError(null)}>
          {exportError}
        </Alert>
      )}

      {programs === null ? (
        <Box display="flex" justifyContent="center" py={8}>
          <CircularProgress />
        </Box>
      ) : ladder.rows.length === 0 ? (
        <Paper variant="outlined" sx={{ p: 4, textAlign: 'center', borderRadius: 2 }}>
          <Typography color="text.secondary">{t('programLadder.empty')}</Typography>
        </Paper>
      ) : (
        <>
          <Stack spacing={1} sx={{ mb: 2 }}>
            {/* UNA RAMA POR TIPO. El ternario de antes mandaba al mensaje de
                «ciclo» todo lo que no fuera un predecesor ausente, así que un
                tipo nuevo se habría anunciado con el texto equivocado — un
                aviso que miente es peor que ninguno. */}
            {ladder.problems.map((p, i) =>
              p.kind === 'missing_predecessor' ? (
                <Alert key={i} severity="warning">
                  {t('programLadder.problems.missingPredecessor', { program: nameOf(p.programId) })}
                </Alert>
              ) : p.kind === 'mixed_year_declaration' ? (
                <Alert key={i} severity="warning">
                  {t('programLadder.problems.mixedYear', {
                    programs: p.programIds.map(nameOf).join(', '),
                    count: p.programIds.length,
                  })}
                </Alert>
              ) : (
                <Alert key={i} severity="error">
                  {t('programLadder.problems.cycle', {
                    chain: p.programIds.map(nameOf).join(' → '),
                  })}
                </Alert>
              ),
            )}
            {!ladder.rows.some((r) => r.inChain) && (
              <Alert severity="info">{t('programLadder.noChain')}</Alert>
            )}
            {ladder.misplaced.length > 0 && (
              <Alert severity="warning">
                {t('programLadder.misplacedSummary', { count: ladder.misplaced.length })}
              </Alert>
            )}
          </Stack>

          <Paper variant="outlined" sx={{ borderRadius: 2, overflowX: 'auto' }}>
            <Box
              sx={{
                display: 'grid',
                gridTemplateColumns,
                // Full width, equal year columns; below the minimum the Paper scrolls.
                width: '100%',
                minWidth: NAME_COL + ladder.columnCount * TERM_COL,
              }}
            >
              {/* Header */}
              <HeaderCell sticky>{t('programLadder.program')}</HeaderCell>
              {ladder.columns.map((c) => (
                <HeaderCell key={c.index}>{columnName(c.index)}</HeaderCell>
              ))}

              {/* One row per program */}
              {ladder.rows.map((row) => {
                const rowCourseIds = row.terms.flatMap((tm) => tm.courseIds);
                // A cumulative award adds up the plans below it: each course once.
                const awardCourseIds =
                  row.includedProgramIds.length > 0
                    ? [
                        ...new Set([
                          ...ladder.rows
                            .filter((r) => row.includedProgramIds.includes(r.programId))
                            .flatMap((r) => r.terms.flatMap((tm) => tm.courseIds)),
                          ...rowCourseIds,
                        ]),
                      ]
                    : null;
                return (
                  <Box key={row.programId} sx={{ display: 'contents' }}>
                    <Box
                      sx={{
                        ...cellSx,
                        position: 'sticky',
                        left: 0,
                        zIndex: 1,
                        bgcolor: 'background.paper',
                      }}
                    >
                      <Link
                        component="button"
                        variant="body2"
                        fontWeight={700}
                        textAlign="left"
                        underline="hover"
                        onClick={() => navigate(`/academic/programs/${row.programId}`)}
                      >
                        {row.name}
                      </Link>
                      <Typography variant="caption" color="text.secondary" display="block">
                        {row.termCount === 1
                          ? columnName(row.startColumn)
                          : `${columnName(row.startColumn)} – ${columnName(
                              row.startColumn + row.termCount - 1,
                            )}`}
                      </Typography>
                      <Typography variant="caption" fontWeight={600} display="block">
                        {t('programLadder.credits', { count: creditsOf(rowCourseIds) })}
                      </Typography>
                      {awardCourseIds && (
                        <Tooltip
                          title={t('programLadder.cumulativeTip', {
                            programs: row.includedProgramIds.map(nameOf).join(', '),
                          })}
                        >
                          <Chip
                            size="small"
                            color="primary"
                            variant="outlined"
                            label={t('programLadder.cumulative', {
                              count: creditsOf(awardCourseIds),
                            })}
                            sx={{ mt: 0.75, height: 20, fontSize: 11 }}
                          />
                        </Tooltip>
                      )}
                    </Box>

                    {ladder.columns.map((c) => {
                      // FILTER Y NO FIND: por años, un programa puede poner los
                      // dos semestres del mismo año en esta columna, y `find`
                      // habría dibujado sólo el primero y perdido el otro en
                      // silencio.
                      const terms = row.terms.filter((tm) => tm.column === c.index);
                      const inSpan =
                        c.index >= row.startColumn && c.index < row.startColumn + row.termCount;
                      return (
                        <Box
                          key={c.index}
                          sx={{
                            ...cellSx,
                            bgcolor: inSpan ? 'action.hover' : 'transparent',
                          }}
                        >
                          {/* LOS SEMESTRES SE APILAN, NO SE PARTEN EN COLUMNAS.
                              Partir la celda en dos mitades le quitaba la mitad
                              del ancho a los nombres de las materias —«Panorama
                              del NT II» caía en tres líneas— para resolver un
                              caso que no ocurre: los dos programas que comparten
                              el año están en FILAS distintas, así que las dos
                              mitades nunca se usaban a la vez. El semestre se
                              dice con una etiqueta, que no cuesta ancho. */}
                          <Stack spacing={1}>
                            {terms.map((term, i) => (
                              <Stack
                                key={i}
                                spacing={0.75}
                                sx={
                                  i > 0
                                    ? {
                                        pt: 1,
                                        // Punteada y no sólida: el borde sólido
                                        // separa AÑOS, y dibujarlas igual haría
                                        // parecer que la escalera tiene el doble.
                                        borderTop: '1px dashed',
                                        borderColor: 'divider',
                                      }
                                    : {}
                                }
                              >
                                {term.semester !== undefined && (
                                  <Typography
                                    variant="caption"
                                    color="text.disabled"
                                    sx={{ fontSize: 10, letterSpacing: 0.4, lineHeight: 1 }}
                                  >
                                    {t('programLadder.semesterShort', { n: term.semester })}
                                  </Typography>
                                )}
                                {term.courseIds.map((id) => (
                                  <CourseCard
                                    key={id}
                                    course={courses.get(id)}
                                    area={areas[id]}
                                    otherColumns={(misplacedById.get(id) ?? [])
                                      .filter((col) => col !== c.index)
                                      .map(columnName)}
                                  />
                                ))}
                              </Stack>
                            ))}
                          </Stack>
                        </Box>
                      );
                    })}
                  </Box>
                );
              })}

              {/* Footer: credits per column, each course once */}
              <Box sx={{ ...footerSx, position: 'sticky', left: 0, zIndex: 1 }}>
                <Typography variant="caption" fontWeight={700}>
                  {t('programLadder.columnTotal')}
                </Typography>
              </Box>
              {ladder.columns.map((c) => (
                <Box key={c.index} sx={footerSx}>
                  <Typography variant="caption" fontWeight={700}>
                    {t('programLadder.credits', { count: creditsOf(c.courseIds) })}
                  </Typography>
                </Box>
              ))}
            </Box>
          </Paper>

          {usedAreas.length > 0 && (
            <Stack direction="row" flexWrap="wrap" alignItems="center" gap={1} sx={{ mt: 1.5 }}>
              <Typography variant="caption" color="text.secondary" sx={{ mr: 0.5 }}>
                {t('program.curriculum.malla.legend')}
              </Typography>
              {usedAreas.map((area) => (
                <Chip
                  key={area}
                  size="small"
                  label={tCap(`subjectAreas.${area}`)}
                  sx={{
                    height: 20,
                    fontSize: 11,
                    bgcolor: 'transparent',
                    border: `1px solid ${AREA_COLOR[area]}`,
                    '& .MuiChip-label': { color: AREA_COLOR[area] },
                  }}
                />
              ))}
            </Stack>
          )}
        </>
      )}
    </Box>
  );
}

const cellSx = {
  p: 1,
  borderTop: '1px solid',
  borderLeft: '1px solid',
  borderColor: 'divider',
  minHeight: 64,
} as const;

const footerSx = {
  ...cellSx,
  minHeight: 0,
  bgcolor: 'background.default',
} as const;

function HeaderCell({ children, sticky }: { children: React.ReactNode; sticky?: boolean }) {
  return (
    <Box
      sx={{
        px: 1,
        py: 1.25,
        borderLeft: '1px solid',
        borderColor: 'divider',
        bgcolor: 'background.default',
        ...(sticky ? { position: 'sticky', left: 0, zIndex: 2 } : {}),
      }}
    >
      <Typography variant="subtitle2" fontWeight={700} color="text.secondary">
        {children}
      </Typography>
    </Box>
  );
}

function CourseCard({
  course,
  area,
  otherColumns,
}: {
  course: CourseInfo | undefined;
  area: SubjectAreaCode | undefined;
  /** Where the same course sits in the other programs that list it. */
  otherColumns: string[];
}) {
  const { t } = useTranslation('sis');
  return (
    <Paper
      variant="outlined"
      sx={{
        p: 0.75,
        borderRadius: 1.5,
        borderLeft: `4px solid ${area ? AREA_COLOR[area] : NO_AREA_COLOR}`,
      }}
    >
      <Stack direction="row" justifyContent="space-between" alignItems="baseline" gap={0.5}>
        <Typography variant="caption" fontWeight={700} noWrap>
          {course?.code || t('program.curriculum.malla.unknownCourse')}
        </Typography>
        {course && (
          <Typography variant="caption" color="text.secondary" whiteSpace="nowrap">
            {t('programLadder.creditsShort', { n: course.credits })}
          </Typography>
        )}
      </Stack>
      {course?.name && (
        <Typography variant="caption" color="text.secondary" display="block" lineHeight={1.25}>
          {course.name}
        </Typography>
      )}
      {course?.inactive && (
        <Chip
          size="small"
          color="warning"
          variant="outlined"
          label={t('program.curriculum.malla.inactiveCourse')}
          sx={{ mt: 0.5, height: 18, fontSize: 10 }}
        />
      )}
      {otherColumns.length > 0 && (
        <Tooltip title={t('programLadder.misplacedTip', { columns: otherColumns.join(', ') })}>
          <Chip
            size="small"
            color="warning"
            label={t('programLadder.misplaced')}
            sx={{ mt: 0.5, height: 18, fontSize: 10 }}
          />
        </Tooltip>
      )}
    </Paper>
  );
}
